import React, { useState, useEffect, useCallback } from 'react';
import { FaSync, FaTimes, FaFilter } from 'react-icons/fa';
import { formatDate } from '../utils/dateUtils';
import { searchFranchisees } from '../services/franchiseeService';
import { debounce } from 'lodash';
import { InputField, SelectField } from './fields';
import { isDemoMode } from '../utils/demoMode';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'AUTHORISED', label: 'Awaiting Payment' },
  { value: 'PAID', label: 'Paid' },
];

// Reusable debounced search helper
const useSearch = (searchFunction, options = {}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const optionsString = JSON.stringify(options);

  const debouncedFetch = useCallback(
    debounce(async (term, fetchOptions) => {
      if (!searchFunction || term.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      setIsSearching(true);
      try {
        const data = await searchFunction({ searchString: term, ...fetchOptions });
        const results = data.franchisees || [];
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [searchFunction]
  );

  useEffect(() => {
    const currentOptions = JSON.parse(optionsString);
    debouncedFetch(searchTerm, currentOptions);
    return () => debouncedFetch.cancel();
  }, [searchTerm, optionsString, debouncedFetch]);

  return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};

const FranchiseeInvoiceTabContent = ({
  invoices,
  loading,
  error,
  isSyncing,
  onSync,
  onRowClick,
  currentPage,
  pageSize,
  hasNextPage,
  onPageChange,
  onPageSizeChange,
  isNavigating,
  filterFseCode,
  filterStatus,
  sortDirection,
  onFilterChange,
  onClearFilters,
  onSortDirectionChange
}) => {
  const franchiseeSearch = useSearch(searchFranchisees);

  // Local state for display value
  const [fseDisplayValue, setFseDisplayValue] = useState(filterFseCode || '');

  useEffect(() => {
    if (filterFseCode) {
      franchiseeSearch.setShowSuggestions(false);
    }
  }, [filterFseCode, franchiseeSearch]);

  const handleFseInputChange = (value) => {
    setFseDisplayValue(value);
    franchiseeSearch.setSearchTerm(value);
    onFilterChange('fseCode', null);
    if (value.length < 3) {
      franchiseeSearch.setSuggestions([]);
      franchiseeSearch.setShowSuggestions(false);
    }
  };

  const handleFseSelect = (fse) => {
    const contactName = `${fse.firstName || ''} ${fse.lastName || ''}`.trim();
    const displayName = `${fse.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
    setFseDisplayValue(displayName);
    franchiseeSearch.setSuggestions([]);
    franchiseeSearch.setShowSuggestions(false);
    onFilterChange('fseCode', fse.fseCode, { autoApply: true });
  };

  const handleApplyFilters = () => {
    onFilterChange('apply');
  };

  const handleClearFiltersLocal = () => {
    setFseDisplayValue('');
    franchiseeSearch.setSearchTerm('');
    franchiseeSearch.setSuggestions([]);
    franchiseeSearch.setShowSuggestions(false);
    onClearFilters();
  };

  const renderErrorMessage = (message) => {
    if (!message) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = String(message).split(urlRegex);
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={`error-link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-red-800 break-all"
          >
            {part}
          </a>
        );
      }
      return <span key={`error-text-${index}`}>{part}</span>;
    });
  };

  const getStatusPillProps = (status) => {
    const normalized = (status || '').toUpperCase();
    const pillClass = normalized === 'DELETED'
      ? 'bg-red-100 text-red-800'
      : normalized === 'VOIDED'
      ? 'bg-gray-100 text-gray-800'
      : normalized === 'PAID'
      ? 'bg-green-100 text-green-800'
      : normalized === 'AUTHORISED'
      ? 'bg-orange-100 text-orange-600'
      : 'bg-gray-100 text-gray-800';

    const label = normalized === 'AUTHORISED'
      ? 'Awaiting Payment'
      : normalized === 'PAID'
      ? 'Paid'
      : (normalized || 'N/A');

    return { pillClass, label };
  };

  const formatAmount = (amount) => {
    if (amount == null || isNaN(amount)) return 'N/A';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div>
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded break-words whitespace-normal">
          {renderErrorMessage(error)}
        </div>
      )}

      {/* Filters Row */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Franchisee Filter */}
          <div className="relative">
            <InputField id="fseFilter" label="Filter by Franchisee"
              labelClassName="!text-xs !text-gray-600"
              autoComplete="off"
              value={fseDisplayValue}
              onChange={(e) => handleFseInputChange(e.target.value)}
              onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
              onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
            />
            {franchiseeSearch.showSuggestions && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {franchiseeSearch.isSearching ? (
                  <li className="px-3 py-2 text-gray-500 text-sm">Searching...</li>
                ) : franchiseeSearch.suggestions.length > 0 ? (
                  franchiseeSearch.suggestions.map(fse => (
                    <li
                      key={fse.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onMouseDown={() => handleFseSelect(fse)}
                    >
                      <div className="font-medium">{fse.firstName} {fse.lastName}</div>
                      <div className="text-xs text-gray-500">{fse.franchiseName || fse.fseCode}</div>
                    </li>
                  ))
                ) : fseDisplayValue.length >= 3 && (
                  <li className="px-3 py-2 text-gray-500 text-sm">No franchisees found.</li>
                )}
              </ul>
            )}
          </div>

          {/* Status Filter */}
          <div>
            <SelectField id="statusFilter" label="Filter by Status"
              labelClassName="!text-xs !text-gray-600"
              value={filterStatus || ''}
              onChange={(e) => onFilterChange('status', e.target.value, { autoApply: true })}
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
          </div>

          {/* Sort by Date */}
          <div>
            <SelectField id="sortDirection" label="Sort by Date"
              labelClassName="!text-xs !text-gray-600"
              value={sortDirection || 'desc'}
              onChange={(e) => onSortDirectionChange(e.target.value)}
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </SelectField>
          </div>

        </div>

        {/* Filter Action Buttons */}
        <div className="flex justify-end space-x-2 mt-3">
          <button
            onClick={handleClearFiltersLocal}
            className="px-3 py-1.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 inline-flex items-center transition duration-300 disabled:opacity-50"
            disabled={loading}
          >
            <FaTimes className="mr-1" size={12} /> Clear
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-3 py-1.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-500 rounded-full shadow-sm inline-flex items-center transition duration-300 disabled:opacity-50"
            disabled={loading || franchiseeSearch.isSearching}
          >
            <FaFilter className="mr-1" size={12} /> Apply
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      {!isDemoMode() && (
      <div className="mb-6 flex items-center justify-start space-x-3">
        <button
          onClick={onSync}
          disabled={isSyncing}
          className={`px-4 py-2 text-sm font-semibold rounded-full shadow-sm inline-flex items-center ${
            isSyncing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-500'
          } transition duration-300`}
        >
          {isSyncing ? (
            <>
              <FaSync className="animate-spin mr-2" />
              Syncing...
            </>
          ) : (
            <>
              <FaSync className="mr-2" />
              Sync with Xero
            </>
          )}
        </button>
      </div>
      )}

      {/* Invoice Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        {loading ? (
          <p className="text-center text-gray-500 py-10">Loading franchisee invoices...</p>
        ) : invoices.length === 0 ? (
          <div className="text-center text-gray-500 p-6">
            <p>No unpaid franchisee invoices found.</p>
          </div>
        ) : (
          <table className="min-w-full leading-normal">
            <thead>
              <tr className="bg-gray-200 text-left text-gray-600 uppercase text-sm">
                <th className="px-5 py-3 border-b-2 border-gray-200">Invoice #</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Franchisee</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Date</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Due Date</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Amount Due</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                return (
                  <tr key={invoice.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onRowClick(invoice.id)}>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap font-medium">{invoice.InvoiceNumber || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{(invoice.franchiseeDisplayName || invoice.franchiseName || invoice.fseCode || 'N/A')}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{formatDate(invoice.Date)}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{formatDate(invoice.DueDate)}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap font-medium">{formatAmount(invoice.AmountDue)}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      {(() => {
                        const { pillClass, label } = getStatusPillProps(invoice.Status);
                        return <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${pillClass}`}>{label}</span>;
                      })()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {!loading && invoices.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              {/* Left side - Page size selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Items per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
                >
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700">
                  {invoices.length < pageSize ? `Showing ${invoices.length}` : `Showing ${pageSize}`}
                </span>
              </div>

              {/* Right side - Page navigation */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isNavigating}
                  className={`px-3 py-1 text-sm font-medium rounded-md inline-flex items-center ${
                    currentPage === 1 || isNavigating
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {isNavigating ? (
                    <>
                      <FaSync className="animate-spin mr-1" size={12} />
                      Previous
                    </>
                  ) : (
                    'Previous'
                  )}
                </button>

                <span className="text-sm text-gray-700 px-2">
                  Page {currentPage}
                </span>

                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={!hasNextPage || isNavigating}
                  className={`px-3 py-1 text-sm font-medium rounded-md inline-flex items-center ${
                    !hasNextPage || isNavigating
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {isNavigating ? (
                    <>
                      <FaSync className="animate-spin mr-1" size={12} />
                      Next
                    </>
                  ) : (
                    'Next'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FranchiseeInvoiceTabContent;
